"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, CircleDot, Crown, Eye, Flame, Heart, MessageCircle, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
interface ChakraItem {
  id: number;
  sanskrit: string;
  german: string;
  zone: string;
  description: string;
  iconName: string;
  orderNumber: string;
}
const chakraData: ChakraItem[] = [{
  id: 1,
  sanskrit: "Muladhara",
  german: "Wurzelchakra",
  zone: "Beckenboden & Steißbein",
  description: "Fundament für Stabilität, Erdung und Urvertrauen. Schenkt Sicherheit und festen Halt im Leben.",
  iconName: "CircleDot",
  orderNumber: "01"
}, {
  id: 2,
  sanskrit: "Svadhisthana",
  german: "Sakralchakra",
  zone: "Unterbauch & Kreuzbein",
  description: "Zentrum für vitale Lebensfreude, Kreativität und emotionale Balance. Fördert gesunden Fluss und Sinnlichkeit.",
  iconName: "Flame",
  orderNumber: "02"
}, {
  id: 3,
  sanskrit: "Manipura",
  german: "Solarplexuschakra",
  zone: "Oberbauch & Magenbereich",
  description: "Sitz des inneren Feuers, der Willenskraft und Selbstwirksamkeit. Stärkt Mut und klare Entscheidungen.",
  iconName: "Activity",
  orderNumber: "03"
}, {
  id: 4,
  sanskrit: "Anahata",
  german: "Herzchakra",
  zone: "Brustraum & Herzebene",
  description: "Zentrum bedingungsloser Liebe, Mitgefühl und tiefer Verbundenheit. Öffnet den Raum für Vergebung.",
  iconName: "Heart",
  orderNumber: "04"
}, {
  id: 5,
  sanskrit: "Vishuddha",
  german: "Halschakra",
  zone: "Kehle & Stimmbänder",
  description: "Ausdruck der eigenen Wahrheit und authentischen Kommunikation. Ermöglicht freies Gehörtwerden.",
  iconName: "MessageCircle",
  orderNumber: "05"
}, {
  id: 6,
  sanskrit: "Ajna",
  german: "Stirnchakra",
  zone: "Zwischen den Augenbrauen",
  description: "Tor zu Intuition, innerer Führung und geistiger Klarheit. Schärft den Blick über das Sichtbare hinaus.",
  iconName: "Eye",
  orderNumber: "06"
}, {
  id: 7,
  sanskrit: "Sahasrara",
  german: "Kronenchakra",
  zone: "Scheitel & Schädeldecke",
  description: "Verbindung zum höheren Bewusstsein und universeller Einheit. Vollendet ganzheitliche Ruhe und Frieden.",
  iconName: "Crown",
  orderNumber: "07"
}];
function ChakraIcon({
  name
}: {
  name: string;
}) {
  const iconProps = {
    className: "size-5 text-primary"
  };
  switch (name) {
    case "CircleDot":
      return <CircleDot {...iconProps} />;
    case "Flame":
      return <Flame {...iconProps} />;
    case "Activity":
      return <Activity {...iconProps} />;
    case "Heart":
      return <Heart {...iconProps} />;
    case "MessageCircle":
      return <MessageCircle {...iconProps} />;
    case "Eye":
      return <Eye {...iconProps} />;
    case "Crown":
      return <Crown {...iconProps} />;
    default:
      return <Sparkles {...iconProps} />;
  }
}
export default function Chakras() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="chakrahero" data-nav="light" className="relative w-full bg-accent/40 py-16 md:py-24 border-b border-border/50 text-foreground overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-secondary text-secondary-foreground text-xs uppercase tracking-wider font-medium mb-4">
            <Sparkles className="size-3.5" />
            <span>Ganzheitliche Energiearbeit</span>
          </div>

          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-normal tracking-tight text-foreground text-balance">Die sieben Chakren: Reinigung und Heilung durch Therapie</h1>

          <p className="mt-4 font-sans text-base sm:text-lg text-foreground/80 leading-relaxed text-balance">Chakren sind feinstoffliche Energiezentren entlang der Wirbelsäule. Sind sie im harmonischen Fluss, fühlen wir uns geerdet, emotional stabil und voller innerer Ruhe. Entdecke die sieben Hauptzentren für deine persönliche Entfaltung.</p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-5">
          {chakraData.map((chakra, i) => <motion.div key={chakra.id} data-index={i} variants={itemVariants} className="group relative flex flex-col justify-between bg-card text-card-foreground rounded-lg p-5 shadow-sm border border-border/60 border-l-4 border-l-primary/60 transition-[box-shadow,transform] duration-200 hover:shadow-md hover:-translate-y-0.5">
              <div>
                <div className="flex items-center justify-between gap-2 pb-3 mb-3 border-b border-border/40">
                  <div className="size-9 rounded-lg bg-secondary/80 flex items-center justify-center">
                    <ChakraIcon name={chakra.iconName} />
                  </div>
                  <span className="font-mono text-xs text-muted-foreground font-semibold">
                    {chakra.orderNumber}
                  </span>
                </div>

                <div className="space-y-1 mb-3">
                  <span className="text-[11px] uppercase tracking-wider text-primary font-medium block">
                    {chakra.german}
                  </span>
                  
                  <p className="text-xs text-muted-foreground font-medium">
                    {chakra.zone}
                  </p>
                </div>

                <p className="font-sans text-xs sm:text-[13px] text-foreground/75 leading-relaxed">
                  {chakra.description}
                </p>
              </div>

              <div className="pt-4 mt-4 border-t border-border/30">
                <Link to="/ReikiTherapyPage" className="text-xs font-medium text-primary hover:text-primary/80 transition-colors inline-flex items-center gap-1.5">
                  <span>Ausgleichen</span>
                  <span aria-hidden="true">&rarr;</span>
                </Link>
              </div>
            </motion.div>)}
        </motion.div>

        <div className="mt-14 text-center flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button asChild size="lg" className="rounded-lg shadow-sm">
            <Link to="/ReikiTherapyPage">
              Reiki &amp; Chakra-Ausgleich kennenlernen
            </Link>
          </Button>
          <Button asChild variant="outline" size="lg" className="rounded-lg bg-background shadow-xs hover:bg-accent">
            <Link to="/ContactPage">Termin anfragen</Link>
          </Button>
        </div>
      </div>
    </section>;
}